import { useMemo } from 'react'
import {
  Box,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core'
import Fwd from '@material-ui/icons/FastForward'
import Rwd from '@material-ui/icons/FastRewind'
import Dot from '@material-ui/icons/FiberManualRecord'
import PendingI from '@material-ui/icons/Remove'
import { INVOICE_STATUS } from '../../../../constants'
import { IInvoice } from 'models/invoices.models'
import { toCurrency } from 'lib/helpers/utilities'
import { useCFDIFinderContainerContext } from 'containers/Invoices/NewCFDI/CFDIFinderContainer/CFDIFinderContainer'
import './styles.scss'
import { getFormattedDate } from 'lib/helpers/shared.helpers'

const movementTypes: Record<string, any> = {
  I: {
    label: 'Ingreso',
    className: 'income',
    Icon: Rwd,
  },
  E: {
    label: 'Egreso',
    className: 'egress',
    Icon: Fwd,
  },
  default: {
    label: 'Pendiente',
    className: 'pending',
    Icon: PendingI,
  },
}

const formatMovement = (
  type: string,
  _row: any,
  addTooltip: boolean = false,
  returnLabel: boolean = false
) => {
  const { label, className, Icon } =
    movementTypes[type] || movementTypes.default

  if (returnLabel) return label

  if (addTooltip) {
    return (
      <span className="status-container">
        <Tooltip title={label}>
          <Icon className={`status-icon ${className}`} color="inherit" />
        </Tooltip>
      </span>
    )
  }
}

const formatStatus = (
  status: number,
  row: any,
  addTooltip: boolean = false,
  returnLabel: boolean = false
) => {
  let label = 'Pendiente'
  let className = 'pending'
  let Icon = PendingI

  if (!row?.uuid) {
    label = 'No timbrado'
  } else if (status === INVOICE_STATUS.ACTIVE.id) {
    label = 'Vigente'
    className = 'active'
    Icon = Dot
  } else if (status === INVOICE_STATUS.CANCELED.id) {
    label = 'Cancelado'
    className = 'canceled'
    Icon = Dot
  } else if (status === INVOICE_STATUS.PENDING.id) {
    label = 'Pendiente'
    className = 'pending'
    Icon = Dot
  }

  if (returnLabel) return label

  if (addTooltip) {
    return (
      <span className="status-container">
        <Tooltip title={label}>
          <Icon className={`status-icon ${className}`} color="inherit" />
        </Tooltip>
      </span>
    )
  }

  return (
    <span className="status-container">
      <Icon className={`status-icon ${className}`} color="inherit" />
    </span>
  )
}

interface CellInterface {
  id: string
  label: string
  getter: (row: IInvoice) => any
  options?: TableCellProps
}

const Row = ({ row, cells }: { row: IInvoice; cells: CellInterface[] }) => {
  const { onSelect } = useCFDIFinderContainerContext()

  const isDisabled = useMemo(() => {
    if (
      row.status === INVOICE_STATUS.CANCELED.id ||
      row.imported === undefined ||
      row.uuid === undefined
    )
      return true
    return false
  }, [row])

  const handleClick = () => {
    if (isDisabled) return
    onSelect(row)
  }

  if (isDisabled) return null

  return (
    <TableRow
      hover
      style={{
        cursor: 'pointer',
        opacity: isDisabled ? 0.5 : 1,
      }}
      onClick={handleClick}>
      {cells.map(cell => (
        <TableCell key={cell.id} {...cell.options}>
          {cell.getter(row)}
        </TableCell>
      ))}
    </TableRow>
  )
}

const CFDIFinderInvoicesTable = () => {
  const {
    cfdisLoading,
    getCFDIsQuery: { data },
  } = useCFDIFinderContainerContext()

  const cells: CellInterface[] = [
    {
      id: 'date',
      label: 'Fecha',
      getter: row => {
        return getFormattedDate(row.createdAt)
      },
    },
    {
      id: 'folio',
      label: 'Serie-Folio',
      getter: row => `${row.serie}-${row.folio}`,
    },
    {
      id: 'receptor',
      label: 'Receptor',
      options: {
        width: '30%',
      },
      getter: row => row.clients.bussinessName,
    },
    {
      id: 'tipo',
      label: 'Tipo',
      getter: row => formatMovement(row.type.value, row, true),
    },
    {
      id: 'estatus',
      label: 'Estatus',
      getter: row => formatStatus(row.status, row, true),
    },
    {
      id: 'pago',
      label: 'Pago',
      getter: row => toCurrency(Number(row.total)),
    },
    {
      id: 'enviado',
      label: 'Enviado',
      getter: row => {
        if (!row.sent) return <PendingI />
        return getFormattedDate(row.sent)
      },
    },
  ]

  return (
    <Box marginY={8}>
      <TableContainer component={Paper}>
        {cfdisLoading && <LinearProgress variant="query" color="secondary" />}
        <Table size="small">
          <TableHead>
            <TableRow>
              {cells.map(cell => (
                <TableCell key={cell.id} {...cell.options}>
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.list?.map((row, index) => (
              <Row key={index} row={row} cells={cells} />
            ))}
            {!cfdisLoading && data?.list && data.list.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  padding="normal"
                  style={{
                    padding: 32,
                  }}>
                  No hay facturas para mostrar.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default CFDIFinderInvoicesTable
